import React, { useEffect, useRef, useState } from 'react';

import { CourseImageConstants } from '../../constants/CourseImageConstants';
import { useFileApi } from '../../hooks/useFileApi';

interface CourseLogoProps {
  logoFileId?: string;
  name: string;
  // A closed course shows the shared Closed logo (its own is not even fetched).
  closed?: boolean;
  // Size, shape and visibility. The logo fills it.
  className?: string;
}

// A course's logo, resolved lazily per item (one SAS lookup each), with the shared default logo
// (the GolfPlayed mark, the same one Explore uses) when it has none or until its own has loaded.
// Split out of CourseCell so the member profile can show a logo on a phone too, where CourseCell
// hides it.
//
// The lookup waits until the logo is near the screen. A member's golf lists can run to hundreds of
// courses, and without this every row would ask for its URL the moment the list rendered.
const CourseLogo: React.FC<CourseLogoProps> = ({
  logoFileId,
  name,
  closed = false,
  className = 'h-10 w-10 rounded',
}) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isNearScreen, setIsNearScreen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const { getFileUrl } = useFileApi();

  const wantsLogo = !!logoFileId && !closed;

  useEffect(() => {
    const box = boxRef.current;
    if (!wantsLogo || isNearScreen || box == null) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setIsNearScreen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearScreen(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(box);
    return (): void => observer.disconnect();
  }, [wantsLogo, isNearScreen]);

  useEffect(() => {
    if (!wantsLogo || !isNearScreen || !logoFileId) {
      return;
    }

    let cancelled = false;
    getFileUrl(logoFileId).then((response) => {
      if (!cancelled && response?.data?.url) {
        setLogoUrl(response.data.url);
      }
    });

    return (): void => {
      cancelled = true;
    };
    // getFileUrl is rebuilt every render by useFileApi, so listing it would re-run this on each one
    // and fetch the logo's URL in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoFileId, wantsLogo, isNearScreen]);

  const src = closed
    ? CourseImageConstants.CLOSED_LOGO
    : (logoUrl ?? CourseImageConstants.DEFAULT_LOGO);

  return (
    <div
      ref={boxRef}
      // A real club logo sits on a white plate (many are dark art on a clear background). The default
      // and Closed logos are black tiles of their own, so they get none (see CourseImageConstants).
      className={`flex shrink-0 items-center justify-center overflow-hidden ${
        logoUrl != null && !closed ? 'bg-white' : ''
      } ${className}`}
    >
      <img
        src={src}
        // The default and Closed logos say nothing the name beside them does not.
        alt={logoUrl != null && !closed ? name : ''}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </div>
  );
};

export default CourseLogo;
